import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Compass, Search, BookHeart, Calendar, ShoppingCart,
  PenSquare, Settings, ChefHat, X, Heart,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

// Mobile-only slide-in drawer (the hamburger menu). Outside the shared-docs canonical
// layout (which only specifies header/main/bottom-nav/sidebar), but kept because the
// Cooksmith UX depends on a drawer for category navigation. Hidden md:hidden so it
// never overlaps the desktop fixed Sidebar.
export default function MobileDrawer() {
  const { sidebarOpen, setSidebarOpen, categories, savedCount } = useApp();

  // Close on Escape
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen, setSidebarOpen]);

  const navItems = [
    { to: '/', icon: Compass, label: 'Explore' },
    { to: '/browse', icon: Search, label: 'Browse Recipes' },
    { to: '/cookbook', icon: BookHeart, label: 'My Cookbook' },
    { to: '/meal-plan', icon: Calendar, label: 'Meal Plan' },
  ];

  const utilityItems = [
    { to: '/grocery', icon: ShoppingCart, label: 'Grocery List' },
    { to: '/submit', icon: PenSquare, label: 'Submit a Recipe' },
    { to: '/profile', icon: Settings, label: 'Settings' },
  ];

  const baseLink = 'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors';
  const inactive = 'text-gray-700 dark:text-night-text hover:bg-gray-100 dark:hover:bg-night-card';
  const active = 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400';

  return (
    <div className="md:hidden">
      <div
        onClick={() => setSidebarOpen(false)}
        className={[
          'fixed inset-0 z-40 bg-black/40 transition-opacity',
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        ].join(' ')}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className={[
          'fixed left-0 top-0 z-50 h-full w-72 max-w-[80%] bg-white dark:bg-night-surface',
          'flex flex-col safe-area-top safe-area-bottom shadow-xl transition-transform duration-200',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <div className="px-5 py-5 border-b border-gray-100 dark:border-night-card flex items-center gap-2">
          <ChefHat size={28} className="text-primary-600" aria-hidden="true" />
          <span className="text-lg font-semibold text-gray-900 dark:text-night-text flex-1">Cooksmith</span>
          <button
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
            className="p-2 -mr-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-700 dark:text-night-text"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `${baseLink} ${isActive ? active : inactive}`}
            >
              <Icon size={20} aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}

          <div className="border-t border-gray-100 dark:border-night-card my-3" />

          {utilityItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `${baseLink} ${isActive ? active : inactive}`}
            >
              <Icon size={20} aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}

          <div className="border-t border-gray-100 dark:border-night-card my-3" />

          <h3 className="text-xs font-semibold text-gray-500 dark:text-night-muted uppercase tracking-wide px-3 mb-2">
            Quick Access
          </h3>
          <NavLink
            to="/cookbook"
            onClick={() => setSidebarOpen(false)}
            className={`${baseLink} ${inactive}`}
          >
            <Heart size={18} aria-hidden="true" />
            <span className="flex-1">My Favourites</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-night-card text-gray-700 dark:text-night-text">
              {savedCount}
            </span>
          </NavLink>

          {categories.length > 0 && (
            <>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-night-muted uppercase tracking-wide px-3 mt-4 mb-2">
                Categories
              </h3>
              {categories.map((cat) => (
                <NavLink
                  key={cat.id}
                  to={`/browse?category=${encodeURIComponent(cat.name)}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`${baseLink} ${inactive} text-sm`}
                >
                  <span className="w-2 h-2 rounded-full bg-primary-400" aria-hidden="true" />
                  <span>{cat.name}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>
      </aside>
    </div>
  );
}
